// EMA 9/13/21 signal logger (2026-10-06) — LOG ONLY. Shows what the Pine
// "MA(9/13/21)" strategy would have traded on real CRUDEOIL futures prices
// (backend: ema91321_logger.py, job ema91321_logger). Places no orders.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Download, RefreshCw } from "lucide-react";
import axios, { API, fmt, fmtTime } from "./commoditiesLib";

const MODE_LABELS = {
  long_short: "Long + Short",
  short_only: "Short only (script default)",
  ls_colour: "L+S · candle colour agrees",
  ls_colour_vol: "L+S · colour + volume ≥ 20-bar avg",
  ls_2candle_rsi: "L+S · 2 candles + RSI < 70 (> 30 short)",
  ls_colour_lowvol: "L+S · colour + volume ≤ 20-bar avg",
  // 2026-10-07 — real ATM option replay (backend ema91321_options.py):
  // BUY LIMIT = premium - 5 (10 min), ±₹1,500/lot, 20:00 exit.
  opt_long_short: "OPTIONS · all signals (ATM, ±₹1.5k, 8 PM)",
  opt_colour_lowvol: "OPTIONS · colour + low volume (ATM, ±₹1.5k, 8 PM)",
  // 2026-10-19 — entries only from signals 14:00–20:00 IST (afternoon + evening session)
  ls_colour_lowvol_pm: "L+S · colour + low volume · 2–8 PM entries",
  ls_2candle_rsi_pm: "L+S · 2 candles + RSI · 2–8 PM entries",
  opt_colour_lowvol_pm: "OPTIONS · colour + low volume · 2–8 PM entries",
  opt_2candle_rsi_pm: "OPTIONS · 2 candles + RSI · 2–8 PM entries",
};
const PERIODS = [14, 30, 60];

const numColor = (v) => (v == null ? "var(--text-muted-2)" : v >= 0 ? "var(--bull)" : "var(--bear)");

// Simple view (2026-10-07, chat: "make this simple... display which are only
// profitable in UI... CSV can have full details"): one small table of the
// modes that are net-profitable in the selected period, best PF first.
// Losing modes are only named; everything is in the CSV export.
const profitableModes = (summary) =>
  Object.keys(MODE_LABELS)
    .map((m) => ({ mode: m, s: summary?.modes?.[m]?.stats || {} }))
    .filter((r) => r.s.trades > 0 && r.s.net_rs_per_lot > 0)
    .sort((a, b) => (b.s.profit_factor ?? 99) - (a.s.profit_factor ?? 99));

const ModesTable = ({ rows, selected, onSelect }) => (
  <div className="overflow-x-auto border border-grid">
    <table className="w-full text-[11px] font-mono-data">
      <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
        <tr className="border-b border-grid">
          {["Profitable modes", "Trades", "Win rate", "Profit factor", "Net ₹ / lot", "Max DD ₹ / lot"].map((h) => (
            <th key={h} className="text-left px-2 py-1.5 font-normal">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ mode, s }) => (
          <tr key={mode} onClick={() => onSelect(mode)}
            className={`border-b border-grid/40 cursor-pointer ${selected === mode ? "bg-white/5" : ""}`}>
            <td className="px-2 py-1.5 text-muted-1">{selected === mode ? "▸ " : ""}{MODE_LABELS[mode]}</td>
            <td className="px-2 py-1.5">{s.trades}</td>
            <td className="px-2 py-1.5">{fmt(s.win_rate_pct, 1)}%</td>
            <td className="px-2 py-1.5" style={{ color: "var(--bull)" }}>{s.profit_factor == null ? "—" : fmt(s.profit_factor, 2)}</td>
            <td className="px-2 py-1.5" style={{ color: "var(--bull)" }}>₹{fmt(s.net_rs_per_lot, 0)}</td>
            <td className="px-2 py-1.5" style={{ color: "var(--bear)" }}>₹{fmt(s.max_dd_rs_per_lot, 0)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const EmaSignalLog = () => {
  const [days, setDays] = useState(30);
  const [mode, setMode] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await axios.get(`${API}/commodities/ema-logger/summary`, { params: { days } });
      setSummary(s.data);
      const rows = profitableModes(s.data);
      const m = rows.some((r) => r.mode === mode) ? mode : rows[0]?.mode || null;
      if (m !== mode) setMode(m);
      if (!m) { setTrades([]); return; }
      const t = await axios.get(`${API}/commodities/ema-logger/trades`, { params: { days, mode: m } });
      setTrades(t.data.trades || []);
    } catch (e) {
      /* not signed in / endpoint not deployed yet — render quietly */
    }
  }, [days, mode]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const runNow = async (backfill) => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/commodities/ema-logger/run`, null,
        { params: backfill ? { backfill_days: 30 } : {} });
      const r = data?.CRUDEOIL || {};
      if (r.ok) toast.success(`Logged ${r.contract}: ${r.bars} bars, last ${fmtTime(r.last_bar)}`);
      else toast.error(`Logger: ${r.error || "failed"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logger run failed");
    } finally {
      setRunning(false);
    }
  };

  const rows = profitableModes(summary);
  const hidden = Object.keys(MODE_LABELS).filter((m) => !rows.some((r) => r.mode === m));

  return (
    <section className="space-y-3 pt-4 border-t border-grid" data-testid="ema-signal-log">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-heading text-lg flex items-center gap-2">
            <Activity size={16} /> EMA 9/13/21 signal log · CRUDEOIL 5m
          </h3>
          <div className="text-[10px] text-muted-3 font-mono-data mt-1">
            Log only, no orders · real futures prices · 0.5% stop / 1% target · costs included.
            Judge after ~1 month (PF above 1.3 → paper trade).
            {summary?.first_day && <> · Logging since {summary.first_day}</>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2 py-1 border text-[10px] font-mono-data ${days === d ? "text-muted-1 border-muted-2" : "text-muted-3 border-grid"}`}>
              {d}d
            </button>
          ))}
          <a href={`${API}/commodities/ema-logger/export.csv?days=${Math.max(days, 90)}`}
             className="px-2 py-1 border border-grid text-[10px] font-mono-data text-muted-2 flex items-center gap-1">
            <Download size={11} /> CSV
          </a>
          {summary?.is_admin && (
            <>
              <button onClick={() => runNow(false)} disabled={running}
                className="px-2 py-1 border border-grid text-[10px] font-mono-data text-muted-2 flex items-center gap-1 disabled:opacity-50">
                <RefreshCw size={11} className={running ? "animate-spin" : ""} /> Run now
              </button>
              <button onClick={() => runNow(true)} disabled={running}
                title="One-off: replay the current front-month contract since it became front month (up to 30 days)"
                className="px-2 py-1 border border-grid text-[10px] font-mono-data text-muted-2 disabled:opacity-50">
                Backfill
              </button>
            </>
          )}
        </div>
      </div>

      {!summary ? (
        <div className="text-muted-2 font-mono-data text-xs">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-grid p-4 text-xs text-muted-2 font-mono-data">
          No mode is profitable in the last {days} days. Full details for every mode are in the CSV.
        </div>
      ) : (
        <ModesTable rows={rows} selected={mode} onSelect={setMode} />
      )}
      {summary && hidden.length > 0 && (
        <div className="text-[10px] text-muted-3 font-mono-data">
          Not shown (losing or no trades in {days}d): {hidden.map((m) => MODE_LABELS[m]).join(" · ")} — see CSV.
        </div>
      )}

      {mode && (
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-2 font-mono-data pt-1">
          Trades · {MODE_LABELS[mode]}
        </div>
      )}
      {mode && <div className="overflow-x-auto border border-grid">
        <table className="w-full text-[11px] font-mono-data">
          <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">
              {["Signal", "Side", "RSI", "Entry", "Exit", "Reason", "Net ₹/lot"].map((h) => (
                <th key={h} className="text-left px-2 py-1.5 font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 ? (
              <tr><td colSpan={7} className="px-2 py-4 text-center text-muted-3">No trades logged in this period.</td></tr>
            ) : trades.map((t) => (
              <tr key={t._id} className="border-b border-grid/40">
                <td className="px-2 py-1 text-muted-2">{fmtTime(t.signal_ts)}</td>
                <td className="px-2 py-1" style={{ color: t.side === "LONG" ? "var(--bull)" : "var(--bear)" }}
                    title={t.option_symbol || undefined}>
                  {t.side}{t.option_type ? ` · ${t.option_strike} ${t.option_type}` : ""}
                </td>
                <td className="px-2 py-1 text-muted-2">{t.signal_rsi != null ? fmt(t.signal_rsi, 1) : "—"}</td>
                <td className="px-2 py-1">{t.entry_price != null ? `${fmt(t.entry_price, 0)} @ ${fmtTime(t.entry_ts).split(", ")[1] || ""}` : "next bar"}</td>
                <td className="px-2 py-1">{t.exit_price != null ? `${fmt(t.exit_price, 0)} @ ${fmtTime(t.exit_ts).split(", ")[1] || ""}`
                  : t.status === "open" ? <span className="text-muted-3">open · LTP {fmt(t.mark_price, 0)}</span> : "—"}</td>
                <td className="px-2 py-1 text-muted-2">
                  {t.status === "not_filled" ? `not filled (limit ${fmt(t.limit_price, 1)})` : (t.exit_reason || t.status)}
                </td>
                <td className="px-2 py-1" style={{ color: numColor(t.net_rs_per_lot ?? t.unrealized_pts) }}>
                  {t.net_rs_per_lot != null ? `₹${fmt(t.net_rs_per_lot, 0)}`
                    : t.unrealized_pts != null ? `(₹${fmt(t.unrealized_pts * 100, 0)})` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
    </section>
  );
};

export default EmaSignalLog;
